/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { FormProvider, Controller, useForm } from "react-hook-form";
import {
  CountryCode,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "libphonenumber-js/min";
import { getCode } from "country-list";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import clsx from "clsx";

import map from "lodash/map";
import some from "lodash/some";

import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/material/styles";

import { AnimatePresence, motion } from "framer-motion";
import { MuiTelInput } from "mui-tel-input";
import useClickAway from "react-use/lib/useClickAway";

import Title from "../../components/Title";
import Modal from "../../components/Modal";
import BackButton from "../../components/BackButton";
import VirtualKeyboard, {
  LayoutType,
  VirtualKeyboardRef,
} from "../../components/VirtualKeyboard";
import Curve from "../../components/Animations/PageTransitions/Curve";
import AggrementDialog from "../../components/Aggrement/AggrementDialog";
import { useAgreementState } from "../../components/Aggrement/hooks/useAgreementState";

import useIdleEffect from "../../lib/hooks/useIdleEffect";
import {
  useClientPlan,
  useDigitalReceptionCountry,
  useLayout,
} from "../../lib/hooks/useQueryBoard";
import { useTimeoutFunction } from "../../lib/hooks/useTimeoutFunction";
import { useDebounceStatus } from "../../lib/hooks/useDebounceStatus";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { useLanguage, useLanguages } from "../../bit/language";
// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";

import { isVirtualKeyboardEnabled } from "../../config/const";

import {
  QUERY_ACCESS_EVENT,
  QUERY_EMPLOYEES,
} from "../../config/graphql/query";
import { MUTATION_CREATE_GUEST } from "../../config/graphql/mutation";
import OfflineModal from "../../components/Offline/OfflineModal";
import MoveLeft from "../../components/Animations/MoveLeft";

const ManualCheckinGrid = styled(Grid)({});

const ManualCheckinField = styled(motion.div)(({ theme }) => ({
  marginBottom: "1.75rem",
  width: "56.5rem",

  "@media screen and (orientation: landscape)": {
    width: "50rem",
  },
}));

interface FieldValues {
  info: { [key: string]: string };
  contact?: {
    id?: string;
    label?: string;
  };
  files?: string[];
}

interface CreateGuestInput {
  info: { [key: string]: string };
  employee?: string;
  screen?: string;
  acceptedAgreements?: string[];
}

const getFieldType = (
  code: string,
  fields: IManualRegistrationField[],
): ManualRegistrationFieldType | undefined =>
  fields.find((field) => field.name === code || field.code === code)?.type;

const ManualCheckin = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { pathname, state: locationState } = useLocation();

  const mainRoute = /^\/access\/checkin\/manual\/?$/.test(pathname);

  const [language] = useLanguage();
  const languages = useLanguages();

  const { integratedManualCheckin, independentManualCheckin } = useLayout();

  const registrationFields: IManualRegistrationField[] = useMemo(() => {
    if (integratedManualCheckin?.enabled) {
      return integratedManualCheckin?.registration?.fields ?? [];
    }
    if (independentManualCheckin?.enabled) {
      return independentManualCheckin?.registration?.fields ?? [];
    }
    return [];
  }, [integratedManualCheckin, independentManualCheckin]);

  const hasContact = useMemo(
    () => registrationFields.some((field) => field.type === "EmployeeList"),
    [registrationFields],
  );

  const defaultFormValues: Partial<FieldValues> = useMemo(
    () => ({
      info: registrationFields.reduce(
        (acc: { [key: string]: string }, item) => {
          acc[item.name || item.code] = "";

          if (locationState?.[item.name || item.code]) {
            acc[item.name || item.code] =
              locationState?.[item.name || item.code];
          }

          return acc;
        },
        {},
      ),
      contact: {
        label: "",
      },
    }),
    [registrationFields, locationState],
  );

  const drCountry = useDigitalReceptionCountry();

  const drCountryCode = useMemo(
    () => (drCountry ? getCode(drCountry) : null),
    [drCountry],
  );

  const defaultCountry: CountryCode | undefined = useMemo(() => {
    if (!!drCountryCode) {
      return drCountryCode as CountryCode;
    }

    if (["nl", "fr", "de", "pt"].includes(language)) {
      return language.toUpperCase() as CountryCode;
    }
    if (language === "sr") {
      return "RS";
    }
    if (language === "en") {
      return "US";
    }
    return undefined;
  }, [language]);

  const preferredCountries = useMemo(() => {
    return (drCountryCode ? [drCountryCode as CountryCode] : []).concat(
      languages.flatMap((language) => {
        if (["nl", "fr", "de", "pt"].includes(language)) {
          return [language.toUpperCase()];
        }
        if (language === "sr") {
          return ["RS"];
        }
        if (language === "en") {
          return ["US"];
        }

        return [];
      }) as CountryCode[],
    );
  }, [languages]);

  const clientPlan = useClientPlan();

  const { screen, showFirst, unacceptedAgreements } = useAgreementState();

  const [pendingAgreements, setPendingAgreements] = useState<
    IEntranceAgreementFile[]
  >(showFirst && unacceptedAgreements ? unacceptedAgreements : []);

  const [calling, setCalling] = useState<boolean>(false);

  const queryClient = useQueryClient();

  // usePageTracking({
  //   page: "manual_checkin",
  // });

  // const { trackEvent } = useEventTracking();

  useIdleEffect(() => {
    navigate("/", { replace: true });
  }, 60000);

  const { data: employeesData } = useQuery(
    [
      "employees",
      {
        filter: {
          preferences: {
            visitor: {
              enabled: {
                EQ: true,
              },
            },
          },
        },
        pagination: {
          limit: 99999,
        },
      },
    ] as const,
    ({ queryKey: [_key, variables] }) =>
      client.request<{ employees: IMemberEmployee[] }>(
        QUERY_EMPLOYEES,
        variables,
      ),
    {
      enabled: hasContact,
    },
  );

  const employees = useMemo(() => employeesData?.employees, [employeesData]);

  const contactList = useMemo(
    () =>
      map(employees, (employee) => ({
        id: employee.id,
        label: employee.fullName,
      })),
    [employees],
  );

  const [layout, setLayout] = useState<LayoutType>("default-shift");

  const [focusedInput, setFocusedInput] = useState<string | undefined>(
    `info.${registrationFields[0]?.name || registrationFields[0]?.code}`,
  );

  const keyboard = useRef<VirtualKeyboardRef>(null);
  const keyboardBox = useRef<HTMLDivElement>(null);

  const methods = useForm<FieldValues>({
    defaultValues: defaultFormValues,
    mode: "onSubmit",
  });

  const { setValue, handleSubmit } = methods;

  useEffect(() => {
    methods.register("files");
  }, []);

  const {
    data,
    status: requestStatus,
    mutate,
    reset,
  } = useMutation((input: CreateGuestInput) => {
    return client.request<
      { createGuest: IAccessEvent },
      { input: CreateGuestInput }
    >(MUTATION_CREATE_GUEST, {
      input,
    });
  });

  const receiver = data?.createGuest?.receiver;

  const autoEmail = receiver?.availablePreferences?.visitor?.autoChannel?.email;
  const autoSMS = receiver?.availablePreferences?.visitor?.autoChannel?.sms;
  const autoCall = receiver?.availablePreferences?.visitor?.autoChannel?.call;
  const autoPing = receiver?.availablePreferences?.visitor?.autoChannel?.ping;

  const status = useDebounceStatus(requestStatus, 1500);

  const translationKey = useMemo(() => {
    const key = `manualCheckin:status.${status}`;

    if (status !== "success") {
      return key;
    }

    let messageType = "no-email-sms";

    if (autoCall || autoPing) {
      messageType = "call-ping";
    } else if (autoEmail || autoSMS) {
      messageType = "email-sms";
    }

    return `${key}.${messageType}`;
  }, [status, autoCall, autoPing, autoEmail, autoSMS]);

  const onDismiss = useCallback(async () => {
    const id = data?.createGuest?.id;

    const isFree = clientPlan && clientPlan === "free";

    // const autoSMS = receiver?.availablePreferences?.visitor?.autoChannel?.sms;

    /**
     * Prefetch access event so we don't wait when on calling page
     */
    await queryClient
      .prefetchQuery([`access/event/${id}`], () =>
        client.request(QUERY_ACCESS_EVENT, { id }),
      )
      .catch(() => null);

    /**
     * Can the user be navigated to contact page based on permissions and cations
     *
     * - Has to be non free user
     * - Access must belong to a visitor that is checking in
     * - Employee has to have a phone number set
     * - Employee has to have at least one notification channel enabled
     */
    const canContinue =
      !isFree && id && status === "success" && receiver?.phone;

    if (canContinue) {
      if (autoCall || autoPing) {
        setCalling(true);

        return navigate(
          `/access/checkin/manual/contact/${id}/${
            !!autoCall ? "call" : "ping"
          }`,
          {
            replace: true,
          },
        );
      }

      // if (!!autoSMS) {
      //   return navigate("/", { replace: true });
      // }

      if (
        some(
          receiver?.availablePreferences?.visitor?.channel,
          (v) => v === true,
        )
      ) {
        return navigate(`/contact/${id}`, { replace: true });
      }
    }

    /**
     * Reset modal on error state
     */
    if (status === "error") {
      return reset();
    }

    return navigate("/", { replace: true });
  }, [status, data]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 10000;
    }

    if (status === "success") {
      return 3000;
    }

    return 0;
  }, [status]);

  useTimeoutFunction(onDismiss, timeout);

  const getLayoutForFieldAndValue = useCallback(
    (layout: LayoutType = "default", field = "", value = ""): LayoutType => {
      switch (field) {
        case "EmployeeList":
        case "Text": {
          if (!value || value.slice(-1) === " ") {
            return "default-shift";
          }

          return "default";
        }

        case "Phone": {
          return "phone-pad";
        }

        case "Number": {
          return "pin";
        }

        case "Email": {
          return "email";
        }

        default: {
          return layout;
        }
      }
    },
    [],
  );

  useEffect(() => {
    setLayout((prevLayout) =>
      getLayoutForFieldAndValue(prevLayout, registrationFields[0].type, ""),
    );
  }, [registrationFields]);

  const onKeyboardChange = useCallback(
    (value = "") => {
      const input = document.querySelector<HTMLInputElement>(
        `[name="${focusedInput}"]`,
      );

      let selectionStart = Math.max(0, value.length - 1);

      if (input && typeof input.selectionStart === "number") {
        selectionStart = input.selectionStart;
      }

      let prevValue = methods.getValues(focusedInput as any) || "";

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(
          prevLayout,
          getFieldType(
            focusedInput?.split(".").pop() || "Text",
            registrationFields,
          ),
          value,
        ),
      );

      if (focusedInput === "contact") {
        if (prevValue && typeof prevValue.label === "string") {
          prevValue = prevValue.label;
        }

        setValue("contact", { label: value });
      } else {
        setValue(focusedInput as any, value);
      }

      /**
       * Update caret position of text input
       */
      if (input) {
        const position = prevValue.length - value.length;

        const next = Math.max(0, selectionStart - position);

        const type = getFieldType(
          focusedInput?.split(".").pop() || "Text",
          registrationFields,
        );
        if (type === "Phone" || type === "Number") {
          keyboard.current?.setCaretPosition(
            prevValue.length < value.length
              ? prevValue.length + 1
              : value.length,
            prevValue.length < value.length
              ? prevValue.length + 1
              : value.length,
          );
        } else {
          input.setSelectionRange(next, next);
        }
      }
    },
    [focusedInput],
  );

  const onKeyboardKeyPress = useCallback(
    (button = "") => {
      if (button === "{alt}") {
        setLayout(
          (layout) =>
            (layout.endsWith("alt")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-alt`) as LayoutType,
        );
      }

      if (button === "{shift}") {
        setLayout(
          (layout) =>
            (layout.endsWith("shift")
              ? layout.split("-").shift()
              : `${layout.split("-").shift()}-shift`) as LayoutType,
        );
      }
    },
    [focusedInput, layout],
  );

  useClickAway(keyboardBox, (event) => {
    if (
      event.target &&
      ["INPUT", "TEXTAREA"].includes((event.target as HTMLElement)?.tagName)
    ) {
      return;
    }

    setFocusedInput(undefined);
  });

  const onInputFocus = useCallback(
    (
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    ) => {
      const {
        target: { name, value },
      } = event;
      // trackEvent({ type: "input_focus", value: `visitor_${name}` });

      /**
       * Skip if there is no virtual keyboard
       */

      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(
          prevLayout,
          getFieldType(name?.split(".").pop() || "Text", registrationFields),
          value,
        ),
      );

      setFocusedInput(name);
    },
    [],
  );

  const onSubmit = useCallback(
    ({ info, contact, files }: FieldValues) => {
      // trackEvent({ type: "notify_button_click", value: "visitor" });

      let input: CreateGuestInput = {
        info: Object.fromEntries(
          Object.entries(info)
            .filter(([key, value]) => value !== "")
            .map(([key, value]) => {
              const type = getFieldType(key, registrationFields);
              if (type === "Phone") {
                return [key, parsePhoneNumber(value).number];
              }
              return [key, value];
            }),
        ),
        employee: contact?.id,
      };

      if (files) {
        input = {
          ...input,
          screen,
          acceptedAgreements: files,
        };
      }

      mutate(input, {
        onSuccess() {
          // trackEvent({ type: "manual_checkin", value: "success" });
        },
        onError() {
          // trackEvent({ type: "manual_checkin", value: `error` });
        },
      });
    },
    [mutate],
  );

  // eslint-disable-next-line consistent-return
  const onBeforeSubmit = useCallback((variables: FieldValues) => {
    if (
      Array.isArray(unacceptedAgreements) &&
      unacceptedAgreements.length > 0
    ) {
      if (showFirst) {
        return onSubmit({
          ...variables,
          files: unacceptedAgreements.map((a) => a.id),
        });
      }

      return setPendingAgreements(unacceptedAgreements);
    }

    onSubmit(variables);
  }, []);

  /**
   * Synchronize text caret position with virtual keyboard
   */
  const onInputSelect = useCallback(
    (event: React.SyntheticEvent<HTMLInputElement, Event>) => {
      if (!keyboard.current) {
        return;
      }

      const { selectionStart, selectionEnd } = event.target as HTMLInputElement;

      if (
        !(
          typeof selectionStart === "number" && typeof selectionEnd === "number"
        )
      ) {
        return;
      }

      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  /**
   * Synchronize text input with virtual keyboard
   */
  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const {
        target: { name, value, selectionStart, selectionEnd },
      } = event;
      /**
       * Skip if there is no virtual keyboard
       */
      if (!keyboard.current) {
        return;
      }

      setLayout((prevLayout) =>
        getLayoutForFieldAndValue(
          prevLayout,
          getFieldType(name?.split(".").pop() || "Text", registrationFields),
          value,
        ),
      );

      keyboard.current.setInput(value, name);
      keyboard.current.setCaretPosition(selectionStart, selectionEnd);
    },
    [],
  );

  const onCountryInputChange = useCallback((value: string) => {
    if (!keyboard.current) {
      return;
    }
    setLayout((prevLayout) =>
      getLayoutForFieldAndValue(
        prevLayout,
        getFieldType("Phone", registrationFields),
        value,
      ),
    );
    keyboard.current.setInput(value, "phone");
    keyboard.current.setCaretPosition(value.length, value.length);
    setValue("info.phone", value);
    setFocusedInput("info.phone");
  }, []);

  return (
    <Curve>
      <FormProvider {...methods}>
        <div className="d-flex align-items-center flex-column">
          <BackButton
            to={
              integratedManualCheckin?.enabled ? "/access/option/checkin" : "/"
            }
          />
          <Title disableAnimation={!mainRoute}>
            <Typography variant="h1">
              <Trans i18nKey="manualCheckin:title" />
            </Typography>
            <Typography variant="h4" className="font-weight-regular">
              {t("manualCheckin:subtitle")}
            </Typography>
          </Title>
          <div>
            <form
              className="form d-flex flex-column"
              autoComplete="off"
              onSubmit={handleSubmit(onBeforeSubmit)}
            >
              <ManualCheckinGrid
                container
                justifyContent="center"
                alignItems="center"
                columnGap={4}
                // spacing={4}
              >
                {registrationFields.map((field, i) => {
                  if (field.type === "Text" || field.type === "Number")
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                            }}
                            render={({
                              field: { onChange, ref, value, ...rest },
                              fieldState: { error },
                            }) => {
                              return (
                                <TextField
                                  {...rest}
                                  error={!!error}
                                  helperText={error?.message ?? " "}
                                  type={field.type.toLowerCase()}
                                  autoFocus={i === 0}
                                  inputRef={ref}
                                  variant="outlined"
                                  label={
                                    field.label + (field.required ? " *" : "")
                                  }
                                  value={value}
                                  onSelect={onInputSelect}
                                  onChange={(event) => {
                                    const {
                                      target: { value },
                                    } = event;

                                    onInputChange(event);

                                    onChange(value);
                                  }}
                                  className={error && "error__field"}
                                  onFocus={onInputFocus}
                                />
                              );
                            }}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );

                  if (field.type === "Email") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                              pattern: {
                                value:
                                  /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                                message: t("manualCheckin:error.email"),
                              },
                            }}
                            render={({
                              field: { onChange, ref, ...rest },
                              fieldState: { error },
                            }) => (
                              <TextField
                                {...rest}
                                error={!!error}
                                helperText={error?.message ?? " "}
                                inputRef={ref}
                                autoFocus={i === 0}
                                type={
                                  isVirtualKeyboardEnabled ? "text" : "email"
                                }
                                variant="outlined"
                                label={
                                  field.label + (field.required ? " *" : "")
                                }
                                value={rest.value || ""}
                                onSelect={onInputSelect}
                                onChange={(event) => {
                                  const {
                                    target: { value },
                                  } = event;

                                  onInputChange(event);

                                  onChange(value);
                                }}
                                className={error && "error__field"}
                                onFocus={onInputFocus}
                              />
                            )}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "Phone") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name={`info.${field.name || field.code}`}
                            rules={{
                              ...(field.required && {
                                required: t("manualCheckin:error.required"),
                              }),
                              validate: (value) => {
                                return (
                                  !value ||
                                  isValidPhoneNumber(value) ||
                                  t("manualCheckin:error.phone")
                                );
                              },
                            }}
                            render={({
                              field: { ref, onChange, ...rest },
                              fieldState: { error },
                            }) => (
                              <MuiTelInput
                                {...rest}
                                error={!!error}
                                inputRef={ref}
                                variant="outlined"
                                autoFocus={i === 0}
                                helperText={error?.message ?? " "}
                                defaultCountry={defaultCountry}
                                preferredCountries={preferredCountries}
                                label={
                                  field.label + (field.required ? " *" : "")
                                }
                                value={rest.value || ""}
                                onSelect={(e: any) => {
                                  onInputChange(e);
                                  onChange(e.target.value);
                                }}
                                onChange={(input) => {
                                  onChange(input);
                                  onCountryInputChange(input);
                                }}
                                className={error && "error__field"}
                                onFocus={onInputFocus}
                              />
                            )}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  if (field.type === "EmployeeList") {
                    return (
                      <MoveLeft
                        key={field.code}
                        disabled={!mainRoute}
                        delay={0.4 + i * 0.06}
                      >
                        <ManualCheckinField>
                          <Controller
                            name="contact"
                            rules={{
                              required: {
                                value: true,
                                message: t("manualCheckin:error.required"),
                              },
                              validate: {
                                exists: (value) => {
                                  if (!(value && value?.id)) {
                                    return t("manualCheckin:error.required");
                                  }

                                  return true;
                                },
                              },
                            }}
                            render={({
                              field: { onChange, name, value },
                              fieldState: { error },
                            }) => {
                              return (
                                <Autocomplete
                                  clearOnBlur
                                  blurOnSelect
                                  disableClearable
                                  disablePortal
                                  autoFocus={i === 0}
                                  noOptionsText={t("manualCheckin:noOption")}
                                  // PopperComponent={PopperTop}
                                  value={!!(value && value.id) ? value : null}
                                  options={contactList}
                                  getOptionLabel={(option) =>
                                    option.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    value?.id === option?.id
                                  }
                                  renderOption={(props, option) => (
                                    <li {...props} key={option.id}>
                                      {option.label}
                                    </li>
                                  )}
                                  onChange={(_event, newValue, reason) => {
                                    if (reason === "selectOption") {
                                      onChange(newValue);
                                      // trackEvent({
                                      //   type: "contact_select",
                                      //   value: "visitor",
                                      // });
                                    }
                                  }}
                                  onBlur={() => {
                                    const currentValue =
                                      methods.getValues(name);

                                    if (!!(currentValue && currentValue.id)) {
                                      return;
                                    }

                                    if (keyboard.current) {
                                      keyboard.current.setInput("", name);
                                    }

                                    onChange(undefined);
                                  }}
                                  inputValue={
                                    !!(value && value.label) ? value.label : ""
                                  }
                                  renderInput={(params) => {
                                    return (
                                      <TextField
                                        {...params}
                                        error={!!error}
                                        helperText={error?.message ?? " "}
                                        type="text"
                                        name="contact"
                                        variant="outlined"
                                        autoCapitalize="sentences"
                                        label={t(
                                          "manualCheckin:placeholder.contactName",
                                        )}
                                        className={error && "error__field"}
                                        onChange={(event) => {
                                          const {
                                            target: { value },
                                          } = event;

                                          onInputChange(event);

                                          onChange({
                                            label: value,
                                          });
                                        }}
                                        onFocus={onInputFocus}
                                        onSelect={onInputSelect}
                                      />
                                    );
                                  }}
                                />
                              );
                            }}
                          />
                        </ManualCheckinField>
                      </MoveLeft>
                    );
                  }

                  return (
                    <MoveLeft
                      key={field.code}
                      disabled={!mainRoute}
                      delay={0.4 + i * 0.06}
                    >
                      <ManualCheckinField>
                        <Controller
                          name={`info.${field.name || field.code}`}
                          rules={{
                            ...(field.required && {
                              required: t("manualCheckin:error.required"),
                            }),
                          }}
                          render={({
                            field: { onChange, ref, value, ...rest },
                            fieldState: { error },
                          }) => {
                            return (
                              <TextField
                                {...rest}
                                error={!!error}
                                helperText={error?.message ?? " "}
                                inputRef={ref}
                                autoFocus={i === 0}
                                variant="outlined"
                                label={
                                  field.label + (field.required ? " *" : "")
                                }
                                value={value}
                                onSelect={onInputSelect}
                                onChange={(event) => {
                                  const {
                                    target: { value },
                                  } = event;

                                  onInputChange(event);

                                  onChange(value);
                                }}
                                className={error && "error__field"}
                                onFocus={onInputFocus}
                                autoComplete="chrome-off"
                              />
                            );
                          }}
                        />
                      </ManualCheckinField>
                    </MoveLeft>
                  );
                })}
              </ManualCheckinGrid>
              <MoveLeft disabled={!mainRoute} delay={0.6}>
                <motion.div className="mx-auto" style={{ width: "56.5rem" }}>
                  <Button
                    variant="contained"
                    className="w-100"
                    onClick={handleSubmit(onBeforeSubmit)}
                  >
                    {t("manualCheckin:btn")}
                  </Button>
                </motion.div>
              </MoveLeft>
            </form>
          </div>
          {status !== "idle" && !calling && (
            <Modal
              status={status}
              // @ts-ignore
              title={<Trans i18nKey={`${translationKey}.title`} />}
              // @ts-ignore
              message={<Trans i18nKey={`${translationKey}.message`} />}
              onDismiss={onDismiss}
            />
          )}
          {isVirtualKeyboardEnabled && (
            <div
              ref={keyboardBox}
              className={clsx("keyboard ", { keyboard__out: !focusedInput })}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <VirtualKeyboard
                ref={keyboard}
                layoutName={layout}
                inputName={focusedInput}
                onChange={onKeyboardChange}
                onKeyPress={onKeyboardKeyPress}
              />
            </div>
          )}
        </div>
      </FormProvider>
      <AggrementDialog
        aggrements={pendingAgreements}
        onDismiss={() => {
          return navigate("/");
        }}
        onSubmit={(id) => {
          setPendingAgreements((prev) => {
            const [accepted, ...rest] = prev;

            methods.setValue("files", [
              ...(methods.getValues("files") || []),
              id,
            ]);

            if (!showFirst && !rest.length) {
              methods.handleSubmit(onSubmit)();
            }

            return rest;
          });
        }}
      />
      <Outlet />
      <OfflineModal />
    </Curve>
  );
};

export default ManualCheckin;
